import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/pages/legal/LegalLayout";

const SUBPROCESSORS = [
  {
    name: "Stripe",
    entity: "Stripe Payments Europe, Ltd. / Stripe, Inc.",
    purpose: "Subscription billing and, where enabled, card payments for Applications for Payment (Stripe Connect).",
    data: "Account & billing identifiers, subscription status, payer email, transaction amounts. Card details are entered directly with Stripe — we never receive them.",
    region: "EU / USA (Standard Contractual Clauses)",
  },
  {
    name: "Google",
    entity: "Google Ireland Ltd / Google LLC",
    purpose: "Google sign-in (OAuth) for login and invitations, consent-based website analytics (Google Analytics 4), and consent-based advertising conversion measurement (Google Ads).",
    data: "Name and email for sign-in; anonymised/aggregated usage and page-view analytics (analytics only after cookie consent).",
    region: "EU / USA (Standard Contractual Clauses)",
  },
  {
    name: "Zoho Mail",
    entity: "Zoho Corporation B.V. (Zoho Europe)",
    purpose: "Delivery of transactional email (welcome, password reset, and Applications for Payment you choose to send).",
    data: "Recipient email address and email content.",
    region: "EU",
  },
  {
    name: "Resend",
    entity: "Resend, Inc.",
    purpose: "Fallback transactional email delivery when the primary email route is unavailable.",
    data: "Recipient email address and email content.",
    region: "USA (Standard Contractual Clauses)",
  },
  {
    name: "Sentry",
    entity: "Functional Software, Inc. (Sentry, EU region)",
    purpose: "Application error and performance monitoring so we can detect and fix faults.",
    data: "Technical log and diagnostic data and limited user identifiers associated with an error.",
    region: "EU",
  },
  {
    name: "HubSpot",
    entity: "HubSpot, Inc.",
    purpose: "Customer relationship management — prospect, lead and subscription tracking (records are uploaded manually by us).",
    data: "Prospect / lead name, business email and company name.",
    region: "EU / USA (Standard Contractual Clauses)",
  },
  {
    name: "Emergent (hosting, database, storage & AI gateway)",
    entity: "Emergent Labs, Inc.",
    purpose: "Cloud application hosting and container infrastructure, database, file / object storage for uploaded blueprints and exports, and the gateway used to route AI requests.",
    data: "All Client Data, including account data, project and estimating data, and uploaded blueprint files and generated PDF exports.",
    region: "Hosted on major cloud infrastructure (EU / USA) under appropriate safeguards",
  },
  {
    name: "AI model providers (via the Emergent gateway)",
    entity: "OpenAI, LLC · Google LLC (Gemini) · Anthropic, PBC (Claude)",
    purpose: "Power the AI Takeoff Assist and AI Help Assistant features. Requests are only sent when you actively use an AI feature.",
    data: "Only the content you submit to an AI feature (e.g. blueprint-derived text/measurements or chat messages). Not used to train the providers' models.",
    region: "USA (Standard Contractual Clauses)",
  },
];

const Subprocessors = () => (
  <LegalLayout
    title="Sub-processors"
    subtitle="Last updated: 18 June 2026"
  >
    <p>
      Blue Shuck Consulting Ltd uses a small number of trusted third-party providers ("sub-processors") to host
      and operate the Service and to process personal data on our behalf. In line with the UK GDPR and our{" "}
      <Link to="/terms" className="underline">Data Processing Agreement</Link>, we keep this list current and use only
      sub-processors that provide sufficient guarantees to implement appropriate technical and organisational measures.
      Each sub-processor is engaged under written data-processing terms (including Standard Contractual Clauses where data
      is processed outside the UK).
    </p>

    <div className="overflow-x-auto my-8 -mx-4 sm:mx-0" data-testid="subprocessors-table-wrap">
      <table className="w-full text-sm border-collapse min-w-[720px]" data-testid="subprocessors-table">
        <thead>
          <tr className="text-left border-b-2 border-slate-300">
            <th className="py-3 pr-4 font-semibold text-navy align-bottom">Provider</th>
            <th className="py-3 pr-4 font-semibold text-navy align-bottom">Purpose</th>
            <th className="py-3 pr-4 font-semibold text-navy align-bottom">Data processed</th>
            <th className="py-3 pr-4 font-semibold text-navy align-bottom">Location</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((s) => (
            <tr
              key={s.name}
              className="border-b border-slate-200 align-top"
              data-testid={`subprocessor-row-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
            >
              <td className="py-4 pr-4">
                <span className="font-semibold text-navy block">{s.name}</span>
                <span className="text-slate-500 text-xs">{s.entity}</span>
              </td>
              <td className="py-4 pr-4 text-slate-700">{s.purpose}</td>
              <td className="py-4 pr-4 text-slate-700">{s.data}</td>
              <td className="py-4 pr-4 text-slate-700 whitespace-normal">{s.region}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2>Changes to this list</h2>
    <p>
      We may add or replace a sub-processor as the Service evolves. Where we do so, we will update this page and the
      "last updated" date above. If you have an active subscription and would like to be notified in advance of changes
      to our sub-processors, email us at{" "}
      <a href="mailto:support@blueshuckconsulting.com">support@blueshuckconsulting.com</a> and we will add you to our
      notification list. You may object to a new sub-processor on reasonable data-protection grounds as set out in our{" "}
      <Link to="/terms" className="underline">Data Processing Agreement</Link>.
    </p>

    <h2>Questions</h2>
    <p>
      For any questions about how we share and protect your data, please see our{" "}
      <Link to="/privacy" className="underline">Privacy &amp; Cookie Policy</Link> or contact{" "}
      <a href="mailto:support@blueshuckconsulting.com">support@blueshuckconsulting.com</a>.
    </p>
  </LegalLayout>
);

export default Subprocessors;
